import { toast } from "@/components/ui/use-toast";
import { CheckCircle2, Download, FolderInput, Loader2 } from "lucide-react";

type DownloadToastOptions = {
  filename?: string | null;
  kind: "file" | "folder";
};

const getLabel = ({ filename, kind }: DownloadToastOptions) => {
  const fallback = kind === "folder" ? "dossier" : "fichier";
  return filename?.trim() || fallback;
};

const DownloadToastContent = ({
  description,
  filename,
  state,
}: {
  description: string;
  filename?: string | null;
  state: "loading" | "success";
}) => (
  <div className="flex min-w-0 items-start gap-3">
    <div className="mt-0.5 rounded-full bg-[#eef4e8] p-2 text-[#1d5215]">
      {state === "loading" ? (
        <Loader2 className="h-5 w-5 animate-spin" />
      ) : (
        <CheckCircle2 className="h-5 w-5" />
      )}
    </div>
    <div className="min-w-0">
      <p className="text-sm font-semibold text-[#1f2937]">{description}</p>
      {filename ? (
        <p className="mt-1 max-w-[300px] truncate text-xs font-medium text-[#5f5a49]">
          {filename}
        </p>
      ) : null}
    </div>
  </div>
);

export const showDownloadInProgressToast = (options: DownloadToastOptions) =>
  toast({
    title: (
      <DownloadToastContent
        description={
          options.kind === "folder"
            ? "Préparation du dossier en cours"
            : "Téléchargement du fichier en cours"
        }
        filename={getLabel(options)}
        state="loading"
      />
    ),
    description:
      options.kind === "folder"
        ? "L’archive reste en préparation. Cette notification disparaîtra quand le navigateur prendra le relais."
        : "Le téléchargement va démarrer dans quelques instants.",
    className: "border-[#1d5215]/30 bg-white shadow-2xl ring-1 ring-[#1d5215]/15",
    duration: 1_000_000,
  });

export const showDownloadSuccessToast = (options: DownloadToastOptions) =>
  toast({
    title: (
      <DownloadToastContent
        description={
          options.kind === "folder"
            ? "Dossier prêt au téléchargement"
            : "Téléchargement lancé"
        }
        filename={getLabel(options)}
        state="success"
      />
    ),
    className: "border-[#1d5215]/30 bg-white shadow-2xl ring-1 ring-[#1d5215]/15",
    duration: 3500,
  });

export const showDownloadErrorToast = (options: DownloadToastOptions) =>
  toast({
    title: (
      <div className="flex min-w-0 items-start gap-3">
        <div className="mt-0.5 rounded-full bg-red-50 p-2 text-red-700">
          <Download className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold">
            {options.kind === "folder"
              ? "Impossible de télécharger le dossier"
              : "Impossible de télécharger le fichier"}
          </p>
          <p className="mt-1 max-w-[300px] truncate text-xs font-medium">
            {getLabel(options)}
          </p>
        </div>
      </div>
    ),
    variant: "destructive",
    duration: 5000,
  });

export const showFolderImportInProgressToast = ({
  folderName,
}: {
  folderName?: string | null;
}) =>
  toast({
    title: (
      <div className="flex min-w-0 items-start gap-3">
        <div className="mt-0.5 rounded-full bg-[#eef4e8] p-2 text-[#1d5215]">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-[#1f2937]">
            Ajout du dossier en cours
          </p>
          <p className="mt-1 max-w-[300px] truncate text-xs font-medium text-[#5f5a49]">
            {folderName?.trim() || "Dossier importé"}
          </p>
        </div>
      </div>
    ),
    description: "Vos fichiers et sous-dossiers sont en cours d’ajout. Cela peut prendre quelques instants.",
    className: "border-[#1d5215]/30 bg-white shadow-2xl ring-1 ring-[#1d5215]/15",
    duration: 1_000_000,
  });

export const showFolderImportSuccessToast = ({
  folderName,
}: {
  folderName?: string | null;
}) =>
  toast({
    title: (
      <div className="flex min-w-0 items-start gap-3">
        <div className="mt-0.5 rounded-full bg-[#eef4e8] p-2 text-[#1d5215]">
          <CheckCircle2 className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-[#1f2937]">
            Dossier ajouté
          </p>
          <p className="mt-1 max-w-[300px] truncate text-xs font-medium text-[#5f5a49]">
            {folderName?.trim() || "Dossier importé"}
          </p>
        </div>
      </div>
    ),
    className: "border-[#1d5215]/30 bg-white shadow-2xl ring-1 ring-[#1d5215]/15",
    description: "Le dossier est maintenant disponible dans vos documents.",
    duration: 3500,
  });

export const showFolderImportErrorToast = ({
  folderName,
  message,
}: {
  folderName?: string | null;
  message?: string | null;
}) =>
  toast({
    title: (
      <div className="flex min-w-0 items-start gap-3">
        <div className="mt-0.5 rounded-full bg-red-50 p-2 text-red-700">
          <FolderInput className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold">
            Le dossier n’a pas pu être ajouté
          </p>
          <p className="mt-1 max-w-[300px] truncate text-xs font-medium">
            {folderName?.trim() || "Dossier importé"}
          </p>
        </div>
      </div>
    ),
    description: message || "Veuillez réessayer. Si le problème continue, essayez avec un dossier plus léger.",
    variant: "destructive",
    duration: 5000,
  });

export const showDocumentUploadInProgressToast = ({
  fileCount,
  fileName,
}: {
  fileCount: number;
  fileName?: string | null;
}) =>
  toast({
    title: (
      <div className="flex min-w-0 items-start gap-3">
        <div className="mt-0.5 rounded-full bg-[#eef4e8] p-2 text-[#1d5215]">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-[#1f2937]">
            Ajout de fichier en cours
          </p>
          <p className="mt-1 max-w-[300px] truncate text-xs font-medium text-[#5f5a49]">
            {fileCount > 1 ? `${fileCount} fichiers` : fileName?.trim() || "Fichier"}
          </p>
        </div>
      </div>
    ),
    description: "Vos fichiers sont en cours d’ajout. Vous pouvez continuer à naviguer.",
    className: "border-[#1d5215]/30 bg-white shadow-2xl ring-1 ring-[#1d5215]/15",
    duration: 1_000_000,
  });

export const showDocumentUploadSuccessToast = ({
  fileCount,
  fileName,
}: {
  fileCount: number;
  fileName?: string | null;
}) =>
  toast({
    title: (
      <div className="flex min-w-0 items-start gap-3">
        <div className="mt-0.5 rounded-full bg-[#eef4e8] p-2 text-[#1d5215]">
          <CheckCircle2 className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-[#1f2937]">
            Fichier ajouté
          </p>
          <p className="mt-1 max-w-[300px] truncate text-xs font-medium text-[#5f5a49]">
            {fileCount > 1 ? `${fileCount} fichiers` : fileName?.trim() || "Fichier"}
          </p>
        </div>
      </div>
    ),
    description: "Vos documents sont maintenant disponibles.",
    className: "border-[#1d5215]/30 bg-white shadow-2xl ring-1 ring-[#1d5215]/15",
    duration: 3500,
  });

export const showDocumentUploadErrorToast = ({
  fileCount,
  fileName,
  message,
}: {
  fileCount: number;
  fileName?: string | null;
  message?: string | null;
}) =>
  toast({
    title: (
      <div className="flex min-w-0 items-start gap-3">
        <div className="mt-0.5 rounded-full bg-red-50 p-2 text-red-700">
          <FolderInput className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold">
            Le fichier n’a pas pu être ajouté
          </p>
          <p className="mt-1 max-w-[300px] truncate text-xs font-medium">
            {fileCount > 1 ? `${fileCount} fichiers` : fileName?.trim() || "Fichier"}
          </p>
        </div>
      </div>
    ),
    description: message || "Veuillez réessayer. Si le problème continue, essayez avec un fichier plus léger.",
    variant: "destructive",
    duration: 5000,
  });
